import { getProductData, type ProductData } from "~/domains/product/types/ProductData";
import { SlicedImage } from "~/features/productDetails/components/PhaseInOut/SlicedImage/SlicedImage";
import { getPrimaryImage } from "~/utils/product/getPrimaryImage";
import { ImageFormat } from "~/domains/product/types/ProductDataDTO";
import { useTranslation } from "react-i18next";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { styled } from "~/common/mui";

interface Props {
  className?: string;
  discontinued: ProductData | ProductDescriptor;
  variant: "product" | "package";
}

export const DiscontinuationNotification = (props: Props) => {
  const { t } = useTranslation();
  const product = getProductData(props.discontinued);
  const imgUrl = getPrimaryImage(product.images, { imageFormat: ImageFormat.product })?.url ?? "";
  const imgConfig = {
    src: imgUrl,
    sliceSize: props.variant === "product" ? 2 : 1,
    gapSize: props.variant === "product" ? 3 : 1,
  };
  return (
    <Root className={props.className} productVariant={props.variant === "product"}>
      <ImageContainer>
        <StyledSlicedImage productVariant={props.variant === "product"} {...imgConfig} />
      </ImageContainer>
      <Content>
        <Headline>{t("page.pdp.phaseinout.discontinuation.headline.basic")}</Headline>
        <Description productVariant={props.variant === "product"}>
          {props.variant === "package"
            ? t("page.pdp.phaseinout.discontinuation.description.packaging")
            : t("page.pdp.phaseinout.discontinuation.description.product")}
        </Description>
      </Content>
    </Root>
  );
};

const Root = styled("div", {
  shouldForwardProp: (prop) => prop !== "productVariant",
})<{ productVariant: boolean }>(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: `${theme.spacing(0.375)} 0 0 0 ${theme.palette.text.secondary} inset`,
  borderRadius: theme.spacing(1),
  position: "relative",
  background: theme.palette.surfaceContainer.lowest,
  padding: theme.spacing(2),
}));

const ImageContainer = styled("div")(({ theme }) => ({
  height: theme.spacing(8),
  width: theme.spacing(8),
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const StyledSlicedImage = styled(SlicedImage, {
  shouldForwardProp: (prop) => prop !== "productVariant",
})<{ productVariant: boolean }>(({ theme, productVariant }) => ({
  height: productVariant ? theme.spacing(12.5) : theme.spacing(8),
  width: theme.spacing(8),
}));

const Content = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  flex: 1,
}));

const Headline = styled("p")(({ theme }) => ({
  ...theme.typography.titleMedium,
  margin: 0,
  fontWeight: "bold",
}));

const Description = styled("p", {
  shouldForwardProp: (prop) => prop !== "productVariant",
})<{ productVariant: boolean }>(({ theme, productVariant }) => ({
  margin: 0,
  ...(productVariant && {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
  }),
}));
